import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../../layouts/PageContainer';
import PageHeader from '../../components/Nav/PageHeader';
import ajaxConfig from '../../util/remote/ajaxConfig';
import Notification from '../../components/Common/Notification';
import { useConfig } from '../../DataStore/ConfigContext';

const CollectionWalletPage = () => {
  const [form, setForm] = useState({
    env: 'live', // 'sandbox' | 'live'
    walletAddress: '',
    secret: '',
    confirmSecret: '',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [status, setStatus] = useState({ saving: false, testing: false, msg: null, type: null });
  const {getWallet, collectionWallet} = useConfig();

  // Wallet IDs are usually alphanumeric (and sometimes include dashes/underscores). Adjust if yours differs.
  const walletLooksValid = /^[A-Za-z0-9\-_]{8,64}$/.test(form.walletAddress?.trim());
  const secretLooksStrong = form.secret?.trim().length >= 12;
  const secretsMatch = form.secret === form.confirmSecret;

  const canSave = useMemo(() => walletLooksValid && secretLooksStrong && secretsMatch, [
    walletLooksValid, secretLooksStrong, secretsMatch
  ]);

  useEffect(()=>{
    getWallet();
  }, [])

  useEffect(() => {
    if (collectionWallet) { 
        setForm({
          walletAddress: collectionWallet.wallet_address,
          secret: collectionWallet.secret,
          // confirmSecret: wallet.secret,
        });
    }
  }, [collectionWallet]);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setStatus({ saving: true, testing: false, msg: null, type: null });
    const server_response = await ajaxConfig.createWallet({
      address: form.walletAddress?.trim(),
      secret: form.secret?.trim(),
    });
    if(server_response.status === "OK") {
      setStatus({ saving: false, testing: false, msg: server_response.message, type: 'success' });
    } else {
      setStatus({ saving: false, testing: false, msg: server_response.message, type: 'danger' });
    }
  };

  const handleTest = async () => {
    setStatus({ saving: false, testing: true, msg: null, type: null });
    const server_response = await ajaxConfig.pingWallet({
      address: form.walletAddress?.trim(),
      secret: form.secret?.trim(),
    });
    getWallet();
    if(server_response.status === "OK") {
      setStatus({ saving: false, testing: false, msg: server_response.message, type: 'success' });
    } else {
      setStatus({ saving: false, testing: false, msg: server_response.message, type: 'danger' });
    }
  };

  const copyWallet = async () => {
    try { await navigator.clipboard.writeText(form.walletAddress); } catch {}
  };

  return (
    <PageContainer>
      <PageHeader
        title="Collection Wallet"
        breadcrumb={
          <>
            <li className="breadcrumb-item">
              <Link to="/settings">Settings</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Collection Wallet
            </li>
          </>
        }
      />

      <div className="container-fluid">
        {/* Top summary row */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info mb-0">
              <strong>NexumPay Collection Wallet</strong> — Set the <em>Business Wallet Address</em> where all Mobile Money
              payments from hotspot voucher activations will be deposited. Use the <em>Secret</em> generated in your
              <strong> NexumPay mobile app</strong> to sign/verify requests.
            </div>
          </div>
        </div>

        <div className="row">
          {/* Main form */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h5 className="card-title mb-1">Configure NexumPay Business Wallet</h5>
                    <p className="text-muted mb-0">
                      Choose environment, paste your Business Wallet Address, and the Secret from the NexumPay app.
                    </p>
                  </div>
                  <div className="btn-group">
                    <button
                      type="button"
                      className={`btn btn-sm ${form.env === 'sandbox' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => setForm((p) => ({ ...p, env: 'sandbox' }))}
                    >
                      Sandbox
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${form.env === 'live' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setForm((p) => ({ ...p, env: 'live' }))}
                    >
                      Live
                    </button>
                  </div>
                </div>

                {status.msg && <Notification type={status.type} message={status.msg} />}

                <form onSubmit={handleSave}>
                  {/* Wallet Address */}
                  <div className="form-group">
                    <label htmlFor="walletAddress">NexumPay Business Wallet Address</label>
                    <div className="input-group">
                      <input
                        id="walletAddress"
                        name="walletAddress"
                        type="text"
                        className={`form-control ${form.walletAddress && !walletLooksValid ? 'is-invalid' : ''}`}
                        placeholder="e.g. NPW_9XY8A12K…"
                        value={form.walletAddress}
                        onChange={onChange}
                        required
                      />
                      <div className="input-group-append">
                        <button className="btn btn-outline-secondary" type="button" onClick={copyWallet} title="Copy">
                          <i className="far fa-copy" />
                        </button>
                      </div>
                      {form.walletAddress && !walletLooksValid && (
                        <div className="invalid-feedback">Wallet format looks unusual. Please confirm from the NexumPay app.</div>
                      )}
                    </div>
                    <small className="form-text text-muted">
                      This is your **Business Wallet** inside NexumPay. All voucher deposits will settle here.
                    </small>
                  </div>

                  {/* Secret */}
                  <div className="form-group">
                    <label htmlFor="secret">Wallet Address Secret (from NexumPay mobile app)</label>
                    <div className="input-group">
                      <input
                        id="secret"
                        name="secret"
                        type={showSecret ? 'text' : 'password'}
                        className={`form-control ${form.secret && !secretLooksStrong ? 'is-invalid' : ''}`}
                        placeholder="Paste from NexumPay app (min 12 characters)"
                        value={form.secret}
                        onChange={onChange}
                        minLength={12}
                        required
                        autoComplete="new-password"
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowSecret((s) => !s)}
                          title={showSecret ? 'Hide' : 'Show'}
                        >
                          <i className={`fa ${showSecret ? 'fa-eye-slash' : 'fa-eye'}`} />
                        </button>
                      </div>
                      {form.secret && !secretLooksStrong && (
                        <div className="invalid-feedback">Secret is too short—confirm you pasted it fully.</div>
                      )}
                    </div>
                    <small className="form-text text-muted">
                      Open the NexumPay mobile app → Business Wallet → “Generate/Reveal Secret”, then paste it here.
                    </small>
                  </div>

                  {/* Confirm Secret */}
                  <div className="form-group">
                    <label htmlFor="confirmSecret">Confirm Secret</label>
                    <input
                      id="confirmSecret"
                      name="confirmSecret"
                      type={showSecret ? 'text' : 'password'}
                      className={`form-control ${form.confirmSecret && !secretsMatch ? 'is-invalid' : ''}`}
                      placeholder="Re-enter secret to confirm"
                      value={form.confirmSecret}
                      onChange={onChange}
                      required
                      autoComplete="new-password"
                    />
                    {form.confirmSecret && !secretsMatch && (
                      <div className="invalid-feedback">Secrets do not match.</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="d-flex align-items-center">
                    <button type="submit" className="btn btn-primary mr-2" disabled={!canSave || status.saving}>
                      {status.saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                          Saving…
                        </>
                      ) : (
                        'Save Wallet'
                      )}
                    </button>

                    <button type="button" className="btn btn-outline-secondary" onClick={handleTest} disabled={status.testing}>
                      {status.testing ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                          Testing…
                        </>
                      ) : (
                        <>
                          <i className="fa fa-plug mr-1" />
                          Test Connection
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Audit / last change */}
            <div className="card mt-3">
              <div className="card-body py-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="far fa-clock mr-1" />
                  Last updated just now
                </small>
                <Link to="/settings/audit" className="text-muted">
                  View audit log <i className="fa fa-angle-right ml-1" />
                </Link>
              </div>
            </div>
          </div>

          {/* Side help */}
          <div className="col-lg-4">
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title mb-2">What you’ll need</h6>
                <ul className="mb-0 pl-3">
                  <li>Your <strong>NexumPay Business Wallet Address</strong>.</li>
                  <li>The <strong>Wallet Secret</strong> generated in the NexumPay mobile app.</li>
                  <li>Pick <strong>Sandbox</strong> to test before going <strong>Live</strong>.</li>
                </ul>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title mb-2">Security notes</h6>
                <ul className="mb-0 pl-3">
                  <li>Never share your Secret in chat or email.</li>
                  <li>Rotate secrets from the NexumPay app when staff changes.</li>
                  <li>Restrict who can view the Secret in this dashboard.</li>
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h6 className="card-title mb-2">Tip</h6>
                <p className="text-muted mb-2">
                  If your app offers a QR or “Copy to clipboard”, use it to avoid typos when pasting the Secret.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default CollectionWalletPage;
